import DemographicData from './data/DemographicData';
import * as d3 from "d3";
import USElectionData from "./data/USElectionData";
import { Ethnicity } from "./data/Ethnicity";
import {AllUSElectionData} from "./data/StateElectionDataEntry";

const BASE_PATH = process.env.REACT_APP_BASE_PATH || '';
console.log('BASE_PATH', BASE_PATH);

class Dashboard {
    get year(): number {
        return this._year;
    }
    get selectedEthnicities(): Set<Ethnicity> {
        return this._selectedEthnicities;
    }
    get demographicData(): DemographicData {
        return this._demographicData;
    }

    get selectedElectionData(): USElectionData {
        return this._selectedElectionData;
    }

    get getAllElectionData(): AllUSElectionData[] {
        return this._allElectionData;
    }

    get electionDataMap(): Map<number, USElectionData> {
        return this._electionDataMap;
    }

    static get INITIAL_YEAR(): number {
        return this._INITIAL_YEAR;
    }
    get isInitialized(): boolean {
        return this._isInitialized;
    }
    get colorScale(): d3.ScaleQuantize<string, never> {
        this.updateColorScale()
        return this._colorScale;
    }

    get colorScaleLegend(): d3.ScaleQuantize<string, never> {
        // this.updateColorScale()
        return this._colorScaleLegend;
    }

    private static instance: Dashboard;
    private _isInitialized: boolean = false;
    private _year: number;
    public selectedStates: Set<string>;
    private _demographicData: DemographicData;
    private _electionDataMap: Map<number, USElectionData> = new Map<number, USElectionData>();
    private _selectedElectionData: USElectionData;
    private _selectedEthnicities: Set<Ethnicity>;
    private _colorScale: d3.ScaleQuantize<string, never>; // New property for color scale
    private _colorScaleLegend: d3.ScaleQuantize<string, never>;
    private static readonly _INITIAL_YEAR = 1976;
    private static readonly electionYears: number[] = [1976, 1980, 1984, 1988, 1992, 1996, 2000, 2004, 2008, 2012, 2016, 2020];
    private static readonly COLORS = [
        // "#0089f4",
        "#0073ca",
        "#65b4f2",
        "#dbedfc",
        // "#FFFFFF",
        "#edc4c4",
        "#e47f7f",
        "#ce0909"
    ];
    private static readonly THRESHOLDS = 
        //  0.2, 0.4, 0.50, 0.6, 0.8
    //     [0.2 , 0.4 ,0.5, 0.6, 0.8
    //   ];
    //   private static readonly BINS =
       [-0.5, -0.3, -0.1, 0, 0.1, 0.3, 0.5]; // Example bin edges


    private _allElectionData: AllUSElectionData[] = [];
    public constructor(
        year: number = Dashboard._INITIAL_YEAR,
        selectedEthnicities: Set<Ethnicity> = new Set<Ethnicity>([Ethnicity.white]),
        demographicData: DemographicData = new DemographicData([]),
        selectedElectionData: USElectionData = new USElectionData(1976, []),
        electionDataMap: Map<number, USElectionData> = new Map<number, USElectionData>(),
        isInitialized: boolean = false,
        selectedStates: Set<string> = new Set<string>(),
        allElectionData : AllUSElectionData[] = []
    ) {
        this._year = year;
        this._selectedEthnicities = selectedEthnicities;
        this._demographicData = demographicData;
        this._selectedElectionData = selectedElectionData;
        this._allElectionData = allElectionData;
        this._electionDataMap = electionDataMap;
        this._isInitialized = isInitialized;
        this.selectedStates = selectedStates;
        this._colorScale = d3.scaleQuantize<string>()
            .domain(Dashboard.THRESHOLDS) 
            .range(Dashboard.COLORS);
        this._colorScaleLegend = d3.scaleQuantize<string>()
            .domain(Dashboard.THRESHOLDS) 
            .range(Dashboard.COLORS);
        }

    private async loadDemographicData() {
        const file = `${BASE_PATH}/data/demographics/demographics.csv`;
        const response = await fetch(file);
        const text = await response.text();
        this._demographicData = new DemographicData(d3.csvParse(text));
    }

    public static async init(): Promise<Dashboard> {
        console.log('Initializing dashboard');
        console.log('hi');
        console.log('BASE_PATH', BASE_PATH);
        Dashboard.instance = new Dashboard();
        await Dashboard.instance.loadDemographicData()
        await Dashboard.instance.loadAllElectionData();
        Dashboard.instance._selectedElectionData = Dashboard.instance.getElectionData(Dashboard._INITIAL_YEAR);
        Dashboard.instance._isInitialized = true;
        return Dashboard.instance;
    }

    public setYear(year: number): void {
        this._year = year;
        this._selectedElectionData = this._electionDataMap.get(year) as USElectionData;
    }

    public getElectionData(year: number): USElectionData {
        if (this._electionDataMap.has(year)) {
            return this._electionDataMap.get(year) as USElectionData;
        }
        throw new Error(`Election data not found for year ${year}`);
    }

    private async loadAllElectionData() {
        const rawElectionDataList: { [key: number]: any } = {};
        for (const year of Dashboard.electionYears) {
            const response: Response = await fetch(`${BASE_PATH}/data/election_data/election_data_${year}.csv`);
            const text: string = await response.text();
            rawElectionDataList[year] = d3.csvParse(text);
            const processedData = Object.values(rawElectionDataList[year] )
            .filter((entry: any) => entry.state_name !== "Totals")
            .map((entry: any) => ({
                state_name: entry.state,
                total_votes: entry.total_votes,
                dem_votes: entry.dem_votes,
                dem_percentage: entry.dem_percentage,
                dem_electoral_votes: entry.dem_electoral_votes,
                rep_votes: entry.rep_votes,
                rep_percentage: entry.rep_percentage,
                rep_electoral_votes: entry.rep_electoral_votes,
                winner: entry.winner,
                point_lead: entry.point_lead,
                num_voters_per_electoral_vote: entry.num_voters_per_electoral_vote,
                year: year
            }));
            this._allElectionData.push(...processedData);
            this._electionDataMap.set(year, new USElectionData(year, rawElectionDataList[year]));
        }
    }

    public updateColorScale(): void {
        const max = 54; // Assume a max point lead
        const min = -54; // Assume a min point lead
    
        this._colorScale = d3.scaleQuantize<string>()
            .domain([min, max]) // Set domain to the full range
            .range(Dashboard.COLORS); // Map to the defined colors
    
        // For color legend scaling
        this._colorScaleLegend = d3.scaleQuantize<string>()
            .domain([min, max])
            .range(Dashboard.COLORS);
    }


    public addSelectedEthnicity(ethnicity: Ethnicity): void {
        this._selectedEthnicities.add(ethnicity);
    }

    public removeSelectedEthnicity(ethnicity: Ethnicity): void {
        this._selectedEthnicities.delete(ethnicity);
    }

    public setSelectedEthnicities(ethnicities: Set<Ethnicity>): void {
        this._selectedEthnicities = ethnicities;
    }
}

export default Dashboard;
